import React, { useState, useRef } from 'react'
import ArenaColorPickerField, { normalizeArenaHex } from './ArenaColorPickerField'
import { useForm } from 'react-hook-form'
import toast from 'react-hot-toast'
import roomsService from '../services/roomsService'
import { useTranslation } from '../hooks/use-i18n'
import chessRoomSettingsService from '../services/chessRoomSettingsService'
import formula1Service from '../services/formula1Service'
import golfRoomSettingsService from '../services/golfRoomSettingsService'
import { RoomType, LeagueEnum } from '../types/room'
import { TournamentType } from '../types/chess'
import { CHESS_TOURNAMENT_TYPE_CONFIGS } from '../constants/chessTournamentTypes'
import { Upload, Image } from 'lucide-react'
import type { DemoRoomConfig } from '../demo/demoRoomTypes'

interface CreateRoomModalProps {
  onClose: () => void
  onSuccess: () => void
  /** When true, only chess arena creation is shown (no game type choice). */
  chessArenaOnly?: boolean
  /** Mock create flow — no API; calls onDemoSuccess with football room config. */
  demoMode?: boolean
  onDemoSuccess?: (config: DemoRoomConfig) => void
}

type GameType = 'football' | 'chess' | 'formula1' | 'golf'

// Football score points: same defaults as mobileBrc / nest-brc RoomSettingEntity
const FOOTBALL_SCORE_FIELDS: { key: keyof CreateRoomForm; labelKey: string; default: string }[] = [
  { key: 'oneZero', labelKey: 'createRoom.score1_0', default: '1' },
  { key: 'twoZero', labelKey: 'createRoom.score2_0', default: '1' },
  { key: 'twoOne', labelKey: 'createRoom.score2_1', default: '1' },
  { key: 'zeroZero', labelKey: 'createRoom.score0_0', default: '2' },
  { key: 'oneOne', labelKey: 'createRoom.score1_1', default: '2' },
  { key: 'twoTwo', labelKey: 'createRoom.score2_2', default: '2' },
  { key: 'threeZero', labelKey: 'createRoom.score3_0', default: '2' },
  { key: 'threeOne', labelKey: 'createRoom.score3_1', default: '2' },
  { key: 'threeTwo', labelKey: 'createRoom.score3_2', default: '2' },
  { key: 'threeThree', labelKey: 'createRoom.score3_3', default: '3' },
  { key: 'fourZero', labelKey: 'createRoom.score4_0', default: '3' },
  { key: 'fourOne', labelKey: 'createRoom.score4_1', default: '3' },
  { key: 'fourTwo', labelKey: 'createRoom.score4_2', default: '3' },
  { key: 'fourThree', labelKey: 'createRoom.score4_3', default: '3' },
  { key: 'fourFour', labelKey: 'createRoom.score4_4', default: '4' },
  { key: 'fiveAndMore', labelKey: 'createRoom.score5plus', default: '5' },
  { key: 'fiveFiveMore', labelKey: 'createRoom.score5_5plus', default: '5' },
]

// Corporate role types (matching breakroom-chess-react)
const ARENA_DEFAULT_PRIMARY = '#3b9b78'
const ARENA_DEFAULT_SECONDARY = '#e5a54b'
const ARENA_DEFAULT_ACCENT = '#3b9b78'

type ArenaColorPickerKey = 'primary' | 'secondary' | 'accent'

const corporateRoles = [
  'Executive',
  'Operations',
  'Finance & Accounting',
  'Human Resources',
  'Sales',
  'Marketing',
  'Product',
  'Research & Development',
  'Development',
  'Customer Service Support',
  'Legal & Compliance',
  'Procurement & Purchasing',
  'Supply Chain / Logistics',
  'Facilities & Administration',
] as const

interface CreateRoomForm {
  name: string
  type: RoomType
  role: string
  gameType: GameType
  // Football score points (per-score, same as mobileBrc)
  oneZero: string
  twoZero: string
  twoOne: string
  zeroZero: string
  oneOne: string
  twoTwo: string
  threeZero: string
  threeOne: string
  threeTwo: string
  threeThree: string
  fourZero: string
  fourOne: string
  fourTwo: string
  fourThree: string
  fourFour: string
  fiveAndMore: string
  fiveFiveMore: string
  correctResultPoints: string
  correctDrawPoints: string
  championAtStart: string
  championSecondChance: string
  enableFutureChampionStart: boolean
  enableFutureChampionSecondChance: boolean
  enableTopScorer: boolean
  topScorerPoints: string
  winnerAdditionalTime: string
  winnerPenalties: string
  // Chess settings
  chessWinPoints: string
  chessDrawPoints: string
  chessLosePoints: string
  defaultTimeControl: string
  defaultVariant: string
  defaultStockfishLevel: number
  defaultRated: boolean
  tournamentType: TournamentType
  // Formula 1 settings
  position1st: string
  position2nd: string
  position3rd: string
  position4th: string
  position5th: string
  position6th: string
  position7th: string
  position8th: string
  position9th: string
  position10th: string
  // Golf settings
  holeInOnePoints: string
  albatrossPoints: string
  eaglePoints: string
  birdiePoints: string
  parPoints: string
  bogeyPoints: string
  doubleBogeyPoints: string
  fairwayHitPoints: string
  longestDriveBonus: string
  closestToPinBonus: string
}

function footballSettingFromForm(data: CreateRoomForm): DemoRoomConfig['setting'] {
  return {
    oneZero: data.oneZero,
    twoZero: data.twoZero,
    twoOne: data.twoOne,
    zeroZero: data.zeroZero,
    oneOne: data.oneOne,
    twoTwo: data.twoTwo,
    threeZero: data.threeZero,
    threeOne: data.threeOne,
    threeTwo: data.threeTwo,
    threeThree: data.threeThree,
    fourZero: data.fourZero,
    fourOne: data.fourOne,
    fourTwo: data.fourTwo,
    fourThree: data.fourThree,
    fourFour: data.fourFour,
    fiveAndMore: data.fiveAndMore,
    fiveFiveMore: data.fiveFiveMore,
    correctResultPoints: data.correctResultPoints,
    correctDrawPoints: data.correctDrawPoints,
    championAtStart: data.championAtStart,
    championSecondChance: data.championSecondChance,
    enableFutureChampionStart: data.enableFutureChampionStart,
    enableFutureChampionSecondChance: data.enableFutureChampionSecondChance,
    enableTopScorer: data.enableTopScorer,
    topScorerPoints: data.topScorerPoints,
    winnerAdditionalTime: data.winnerAdditionalTime,
    winnerPenalties: data.winnerPenalties,
  }
}

const CreateRoomModal: React.FC<CreateRoomModalProps> = ({
  onClose,
  onSuccess,
  chessArenaOnly = false,
  demoMode = false,
  onDemoSuccess,
}) => {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(false)
  const { register, handleSubmit, watch, setValue, formState: { errors } } = useForm<CreateRoomForm>({
    defaultValues: {
      name: demoMode ? 'World Cup Office Pool' : '',
      type: RoomType.Individual,
      role: 'Executive',
      gameType: chessArenaOnly ? 'chess' : 'football',
      oneZero: '1',
      twoZero: '1',
      twoOne: '1',
      zeroZero: '2',
      oneOne: '2',
      twoTwo: '2',
      threeZero: '2',
      threeOne: '2',
      threeTwo: '2',
      threeThree: '3',
      fourZero: '3',
      fourOne: '3',
      fourTwo: '3',
      fourThree: '3',
      fourFour: '4',
      fiveAndMore: '5',
      fiveFiveMore: '5',
      correctResultPoints: '1',
      correctDrawPoints: '1',
      championAtStart: '20',
      championSecondChance: '10',
      enableFutureChampionStart: demoMode ? true : true,
      enableFutureChampionSecondChance: demoMode ? false : true,
      enableTopScorer: demoMode ? true : false,
      topScorerPoints: '20',
      winnerAdditionalTime: '2',
      winnerPenalties: '3',
      chessWinPoints: '1',
      chessDrawPoints: '0.5',
      chessLosePoints: '0',
      defaultTimeControl: '10+0',
      defaultVariant: 'standard',
      defaultStockfishLevel: 1,
      defaultRated: false,
      tournamentType: chessArenaOnly ? 'arena' : 'arena',
      position1st: '25',
      position2nd: '18',
      position3rd: '15',
      position4th: '12',
      position5th: '10',
      position6th: '8',
      position7th: '6',
      position8th: '4',
      position9th: '2',
      position10th: '1',
      holeInOnePoints: '2',
      albatrossPoints: '8',
      eaglePoints: '5',
      birdiePoints: '3',
      parPoints: '1',
      bogeyPoints: '0',
      doubleBogeyPoints: '-1',
      fairwayHitPoints: '2',
      longestDriveBonus: '2',
      closestToPinBonus: '2',
    }
  })

  const [selectedLeagues, setSelectedLeagues] = useState<LeagueEnum[]>(
    demoMode ? [LeagueEnum.WorldCup2026] : [],
  )
  const gameType = watch('gameType')
  const effectiveGameType = chessArenaOnly ? 'chess' : gameType
  // Arena branding (chess arena only)
  const [arenaLogo, setArenaLogo] = useState<string | null>(null)
  const [arenaBanner, setArenaBanner] = useState<string | null>(null)
  const [arenaPrimaryColor, setArenaPrimaryColor] = useState(ARENA_DEFAULT_PRIMARY)
  const [arenaSecondaryColor, setArenaSecondaryColor] = useState(ARENA_DEFAULT_SECONDARY)
  const [arenaAccentColor, setArenaAccentColor] = useState(ARENA_DEFAULT_ACCENT)
  const [openArenaColorPicker, setOpenArenaColorPicker] = useState<ArenaColorPickerKey | null>(null)
  const arenaLogoInputRef = useRef<HTMLInputElement>(null)
  const arenaBannerInputRef = useRef<HTMLInputElement>(null)
  const handleArenaFileUpload = (e: React.ChangeEvent<HTMLInputElement>, setter: (v: string | null) => void) => {
    const file = e.target.files?.[0]
    if (file) {
      const reader = new FileReader()
      reader.onloadend = () => setter(reader.result as string)
      reader.readAsDataURL(file)
    }
    e.target.value = ''
  }

  const onSubmit = async (data: CreateRoomForm) => {
    try {
      setIsLoading(true)

      if (demoMode) {
        await new Promise((resolve) => setTimeout(resolve, 450))
        onDemoSuccess?.({
          name: data.name,
          type: data.type,
          role: data.role,
          leagues: selectedLeagues.length > 0 ? selectedLeagues : [LeagueEnum.WorldCup2026],
          setting: footballSettingFromForm(data),
        })
        toast.success(t('createRoom.footballRoomCreated'))
        onSuccess()
        return
      }

      if (data.gameType === 'football') {
        // Football room creation – per-score points (same as mobileBrc)
        await roomsService.createRoom({
          name: data.name,
          type: data.type,
          role: data.role,
          gameType: 'football',
          leagues: selectedLeagues,
          setting: {
            oneZero: data.oneZero,
            twoZero: data.twoZero,
            twoOne: data.twoOne,
            zeroZero: data.zeroZero,
            oneOne: data.oneOne,
            twoTwo: data.twoTwo,
            threeZero: data.threeZero,
            threeOne: data.threeOne,
            threeTwo: data.threeTwo,
            threeThree: data.threeThree,
            fourZero: data.fourZero,
            fourOne: data.fourOne,
            fourTwo: data.fourTwo,
            fourThree: data.fourThree,
            fourFour: data.fourFour,
            fiveAndMore: data.fiveAndMore,
            fiveFiveMore: data.fiveFiveMore,
            correctResultPoints: data.correctResultPoints,
            correctDrawPoints: data.correctDrawPoints,
            championAtStart: data.championAtStart,
            championSecondChance: data.championSecondChance,
            enableFutureChampionStart: data.enableFutureChampionStart,
            enableFutureChampionSecondChance: data.enableFutureChampionSecondChance,
            enableTopScorer: data.enableTopScorer,
            topScorerPoints: data.topScorerPoints,
            winnerAdditionalTime: data.winnerAdditionalTime,
            winnerPenalties: data.winnerPenalties,
          }
        })
        toast.success(t('createRoom.footballRoomCreated'))
      } else if (data.gameType === 'chess') {
        // Chess room creation
        const room = await roomsService.createRoom({
          name: data.name,
          type: data.type,
          role: data.role,
          gameType: 'chess',
          leagues: ['Champions League'], // Default league for chess rooms
          defaultTimeControl: data.defaultTimeControl,
          defaultVariant: data.defaultVariant,
          defaultStockfishLevel: data.defaultStockfishLevel,
          defaultRated: data.defaultRated,
          chessTournamentType: (data.tournamentType === 'arena' || data.tournamentType === 'round-robin') ? data.tournamentType : 'arena',
          ...(arenaLogo != null && { arenaLogo }),
          ...(arenaBanner != null && { arenaBanner }),
          arenaPrimaryColor: normalizeArenaHex(arenaPrimaryColor, ARENA_DEFAULT_PRIMARY),
          arenaSecondaryColor: normalizeArenaHex(arenaSecondaryColor, ARENA_DEFAULT_SECONDARY),
          arenaAccentColor: normalizeArenaHex(arenaAccentColor, ARENA_DEFAULT_ACCENT),
        })

        // Create chess room settings
        try {
          await chessRoomSettingsService.create({
            roomId: room._id,
            winPoints: parseFloat(data.chessWinPoints),
            drawPoints: parseFloat(data.chessDrawPoints),
            losePoints: parseFloat(data.chessLosePoints),
            tournamentType: (data.tournamentType === 'arena' || data.tournamentType === 'round-robin') ? data.tournamentType : 'arena',
          })
        } catch (chessError) {
          console.warn('Failed to create chess room settings:', chessError)
          // Don't fail the room creation if settings fail
        }

        toast.success(t('createRoom.chessRoomCreated'))
      } else if (data.gameType === 'formula1') {
        // Formula 1 room creation
        const room = await roomsService.createRoom({
          name: data.name,
          type: data.type,
          role: data.role,
          gameType: 'formula1',
          leagues: [], // Formula 1 doesn't use leagues
        })

        // Create Formula 1 room settings
        try {
          await formula1Service.updateRoomSettings(room._id, {
            position1st: parseFloat(data.position1st) || 25,
            position2nd: parseFloat(data.position2nd) || 18,
            position3rd: parseFloat(data.position3rd) || 15,
            position4th: parseFloat(data.position4th) || 12,
            position5th: parseFloat(data.position5th) || 10,
            position6th: parseFloat(data.position6th) || 8,
            position7th: parseFloat(data.position7th) || 6,
            position8th: parseFloat(data.position8th) || 4,
            position9th: parseFloat(data.position9th) || 2,
            position10th: parseFloat(data.position10th) || 1,
            championAtStart: parseFloat(data.championAtStart) || 20,
            championSecondChance: parseFloat(data.championSecondChance) || 10,
          })
        } catch (formula1Error) {
          console.warn('Failed to create Formula 1 room settings:', formula1Error)
          // Don't fail the room creation if settings fail
        }

        toast.success(t('createRoom.formula1RoomCreated'))
      } else if (data.gameType === 'golf') {
        const room = await roomsService.createRoom({
          name: data.name,
          type: data.type,
          role: data.role,
          gameType: 'golf',
          leagues: [],
        })

        try {
          await golfRoomSettingsService.updateRoomSettings(room._id, {
            holeInOnePoints: parseFloat(data.holeInOnePoints) || 2,
            albatrossPoints: parseFloat(data.albatrossPoints) || 8,
            eaglePoints: parseFloat(data.eaglePoints) || 5,
            birdiePoints: parseFloat(data.birdiePoints) || 3,
            parPoints: parseFloat(data.parPoints) || 1,
            bogeyPoints: parseFloat(data.bogeyPoints) || 0,
            doubleBogeyPoints: parseFloat(data.doubleBogeyPoints) || -1,
            fairwayHitPoints: parseFloat(data.fairwayHitPoints) || 2,
            longestDriveBonus: parseFloat(data.longestDriveBonus) || 2,
            closestToPinBonus: parseFloat(data.closestToPinBonus) || 2,
          })
        } catch (golfError) {
          console.warn('Failed to create golf room settings:', golfError)
        }

        toast.success('Golf room created successfully!')
      }

      onSuccess()
    } catch (error: any) {
      toast.error(error.response?.data?.message || t('createRoom.createRoomFailed'))
    } finally {
      setIsLoading(false)
    }
  }

  const handleLeagueChange = (league: LeagueEnum, checked: boolean) => {
    if (checked) {
      setSelectedLeagues((prev) => {
        const next = [...prev, league]
        if (league === LeagueEnum.WorldCup2026) {
          setValue('enableTopScorer', true)
          setValue('enableFutureChampionStart', true)
          setValue('enableFutureChampionSecondChance', false)
        }
        if (league === LeagueEnum.ChampionsLeague) {
          setValue('enableFutureChampionStart', true)
          setValue('enableFutureChampionSecondChance', true)
        }
        return next
      })
    } else {
      setSelectedLeagues((prev) => {
        const next = prev.filter((l) => l !== league)
        const hasWC = next.includes(LeagueEnum.WorldCup2026)
        const hasCL = next.includes(LeagueEnum.ChampionsLeague)
        setValue('enableTopScorer', hasWC)
        setValue('enableFutureChampionStart', hasWC || hasCL)
        setValue('enableFutureChampionSecondChance', hasCL && !hasWC)
        return next
      })
    }
  }

  const getGameTypeEmoji = (type: GameType) => {
    switch (type) {
      case 'football': return '🏆'
      case 'chess': return '♟️'
      case 'formula1': return '🏎️'
      case 'golf': return '⛳'
      default: return '🏠'
    }
  }

  const getGameTypeName = (type: GameType) => {
    switch (type) {
      case 'football': return t('room.gameFootball')
      case 'chess': return t('room.gameChess')
      case 'formula1': return t('room.gameFormula1')
      case 'golf': return 'Golf'
      default: return t('dashboard.createRoom')
    }
  }

  const getLeagueKey = (league: LeagueEnum) =>
    'createRoom.league' + league.split('-').map(s => s.charAt(0).toUpperCase() + s.slice(1)).join('')

  const getRoleKey = (role: string) =>
    'createRoom.role' + role.replace(/\s*&\s*/g, '').replace(/\s*\/\s*/g, '').replace(/\s+/g, '')

  return (
    <div
      className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4 overflow-y-auto"
      onClick={(e) => { if (e.target === e.currentTarget) onClose() }}
      role="dialog"
      aria-modal="true"
    >
      <div className="w-full max-w-2xl mx-4 glass-strong rounded-2xl p-5 sm:p-6 my-8 max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="flex justify-between items-center mb-6">
          <h2 className="font-display text-display-2 font-bold text-foreground flex items-center gap-2">
            {chessArenaOnly ? `♟️ ${t('dashboard.createChessArena')}` : `${getGameTypeEmoji(gameType)} ${t('createRoom.createRoomTitle', { game: getGameTypeName(gameType) })}`}
          </h2>
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-all" aria-label={t('common.close')}>
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="flex-1 overflow-y-auto min-h-0 space-y-5 pr-1">
          {/* Game Type Selection (hidden when chess arena only or demo mode) */}
          {!chessArenaOnly && !demoMode && (
          <div>
            <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-2 block">{t('createRoom.gameType')}</label>
            <div className="grid grid-cols-3 gap-2">
              {(['football', 'formula1', 'golf'] as GameType[]).map((type) => (
                <button
                  key={type}
                  type="button"
                  onClick={() => setValue('gameType', type)}
                  className={`rounded-xl py-4 flex flex-col items-center gap-1.5 text-nav font-medium transition-all border ${
                    gameType === type
                      ? 'border-primary bg-primary/10 text-foreground shadow-md shadow-primary/10'
                      : 'border-muted/20 bg-muted/5 text-muted-foreground hover:border-muted/40 hover:bg-muted/10'
                  }`}
                >
                  <span className="text-2xl">{getGameTypeEmoji(type)}</span>
                  <span>{getGameTypeName(type)}</span>
                </button>
              ))}
            </div>
            <input type="hidden" {...register('gameType')} />
          </div>
          )}

          {/* Room / Arena Name */}
          <div>
            <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-2 block">
              {chessArenaOnly ? t('createRoom.arenaName') : t('createRoom.roomName')}
            </label>
            <input
              {...register('name', { required: chessArenaOnly ? t('createRoom.arenaNameRequired') : t('createRoom.roomNameRequired') })}
              type="text"
              className="w-full px-4 py-3 rounded-xl bg-background border border-border text-body text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all"
              placeholder={chessArenaOnly ? t('createRoom.placeholderArenaName') : t('createRoom.placeholderRoomName')}
            />
            {errors.name && <p className="mt-2 text-small text-destructive">{errors.name.message}</p>}
          </div>

          {/* Room / Arena Type */}
          <div>
            <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-2 block">
              {chessArenaOnly ? t('createRoom.arenaType') : t('createRoom.roomType')}
            </label>
            <select
              {...register('type', { required: chessArenaOnly ? t('createRoom.arenaTypeRequired') : t('createRoom.roomTypeRequired') })}
              className="w-full px-4 py-3 rounded-xl bg-background border border-border text-body text-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all"
            >
              <option value={RoomType.Individual}>{t('createRoom.roomTypeIndividual')}</option>
              <option value={RoomType.Company}>{t('createRoom.roomTypeCompany')}</option>
            </select>
          </div>

          {/* Role (for Company rooms) */}
          {watch('type') === RoomType.Company && (
            <div>
              <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-2 block">
                {t('createRoom.yourRole')}
              </label>
              <select
                {...register('role', { 
                  required: watch('type') === RoomType.Company ? t('createRoom.roleRequired') : false 
                })}
                className="w-full px-4 py-3 rounded-xl bg-background border border-border text-body text-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all"
              >
                {corporateRoles.map((role) => (
                  <option key={role} value={role}>
                    {t(getRoleKey(role))}
                  </option>
                ))}
              </select>
              {errors.role && (
                <p className="mt-2 text-small text-destructive">{errors.role.message}</p>
              )}
            </div>
          )}

          {/* Football-specific fields */}
          {gameType === 'football' && (
            <>
              <div>
                <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-2 block">
                  {t('createRoom.selectLeagues')}
                </label>
                <div className="grid grid-cols-2 gap-3">
                  {Object.values(LeagueEnum).map((league) => (
                    <label key={league} className="flex items-center p-3 rounded-xl bg-background/80 border border-border hover:border-primary/40 transition-colors cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedLeagues.includes(league)}
                        onChange={(e) => handleLeagueChange(league, e.target.checked)}
                        className="mr-3 w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary/30 focus:ring-2"
                      />
                      <span className="text-nav text-foreground font-medium">
                        {t(getLeagueKey(league))}
                      </span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="glass-card rounded-xl p-4 border border-border">
                <h3 className="text-display-2 font-semibold text-foreground mb-1">{t('createRoom.pointsDistribution')}</h3>
                <p className="text-small text-foreground/80 mb-4">{t('createRoom.pointsDistributionDesc')}</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-4">
                  <div>
                    <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-1 block">{t('createRoom.correctResultPoints')}</label>
                    <input {...register('correctResultPoints', { required: true, min: 0 })} type="number" min="0" placeholder="1" className="w-full px-3 py-2 rounded-xl bg-background border border-border text-body text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all" />
                  </div>
                  <div>
                    <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-1 block">{t('createRoom.correctDrawPoints')}</label>
                    <input {...register('correctDrawPoints', { required: true, min: 0 })} type="number" min="0" placeholder="1" className="w-full px-3 py-2 rounded-xl bg-background border border-border text-body text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all" />
                  </div>
                </div>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {FOOTBALL_SCORE_FIELDS.map(({ key, labelKey, default: d }) => (
                    <div key={key}>
                      <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-1 block">
                        {t(labelKey)}
                      </label>
                      <input
                        {...register(key, { required: true, min: 0 })}
                        type="number"
                        min="0"
                        placeholder={d}
                        className="w-full px-3 py-2 rounded-xl bg-background border border-border text-body text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all"
                      />
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-border space-y-3">
                  <h4 className="text-small font-semibold text-foreground/90 uppercase tracking-wider">{t('createRoom.tournamentExtras')}</h4>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" {...register('enableFutureChampionStart')} className="w-4 h-4" />
                    <span className="text-nav text-foreground">{t('createRoom.enableFutureChampionStart')}</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" {...register('enableFutureChampionSecondChance')} className="w-4 h-4" />
                    <span className="text-nav text-foreground">{t('createRoom.enableFutureChampionSecondChance')}</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" {...register('enableTopScorer')} className="w-4 h-4" />
                    <span className="text-nav text-foreground">{t('createRoom.enableTopScorer')}</span>
                  </label>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 pt-4 border-t border-border">
                  <div>
                    <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-1 block">{t('createRoom.championAtStart')}</label>
                    <input {...register('championAtStart', { required: true, min: 0 })} type="number" min="0" placeholder="20" className="w-full px-3 py-2 rounded-xl bg-background border border-border text-body text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all" />
                  </div>
                  <div>
                    <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-1 block">{t('createRoom.championSecondChance')}</label>
                    <input {...register('championSecondChance', { required: true, min: 0 })} type="number" min="0" placeholder="10" className="w-full px-3 py-2 rounded-xl bg-background border border-border text-body text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all" />
                  </div>
                  <div>
                    <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-1 block">{t('createRoom.topScorerPoints')}</label>
                    <input {...register('topScorerPoints', { required: true, min: 0 })} type="number" min="0" placeholder="20" className="w-full px-3 py-2 rounded-xl bg-background border border-border text-body text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all" />
                  </div>
                  <div>
                    <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-1 block">{t('createRoom.winnerExtraTimePoints')}</label>
                    <input {...register('winnerAdditionalTime', { required: true, min: 0 })} type="number" min="0" placeholder="2" className="w-full px-3 py-2 rounded-xl bg-background border border-border text-body text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all" />
                  </div>
                  <div>
                    <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-1 block">{t('createRoom.winnerPenaltiesPoints')}</label>
                    <input {...register('winnerPenalties', { required: true, min: 0 })} type="number" min="0" placeholder="3" className="w-full px-3 py-2 rounded-xl bg-background border border-border text-body text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30 transition-all" />
                  </div>
                </div>
              </div>
            </>
          )}

          {/* Chess-specific fields */}
          {effectiveGameType === 'chess' && (
            <>
              <div className="glass-card rounded-xl p-4 border border-border">
                <h3 className="text-display-2 font-semibold text-foreground mb-4">♟️ {t('createRoom.chessPointsSystem')}</h3>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-2 block">{t('createRoom.winPoints')}</label>
                    <input
                      {...register('chessWinPoints', { required: true })}
                      type="number"
                      step="0.1"
                      className="w-full px-3 py-2 rounded-xl bg-background border border-border text-body text-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
                    />
                  </div>
                  <div>
                    <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-2 block">{t('createRoom.drawPoints')}</label>
                    <input
                      {...register('chessDrawPoints', { required: true })}
                      type="number"
                      step="0.1"
                      className="w-full px-3 py-2 rounded-xl bg-background border border-border text-body text-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
                    />
                  </div>
                  <div>
                    <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-2 block">{t('createRoom.losePoints')}</label>
                    <input
                      {...register('chessLosePoints', { required: true })}
                      type="number"
                      step="0.1"
                      className="w-full px-3 py-2 rounded-xl bg-background border border-border text-body text-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
                    />
                  </div>
                </div>
              </div>

              <div className="glass-card rounded-xl p-4 border border-border">
                <h3 className="text-display-2 font-semibold text-foreground mb-4">⚙️ {t('createRoom.defaultGameSettings')}</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-2 block">{t('createRoom.timeControl')}</label>
                    <select
                      {...register('defaultTimeControl')}
                      className="w-full px-3 py-2 rounded-xl bg-background border border-border text-body text-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
                    >
                      <option value="1+0">{t('createRoom.timeControl1_0')}</option>
                      <option value="3+0">{t('createRoom.timeControl3_0')}</option>
                      <option value="5+0">{t('createRoom.timeControl5_0')}</option>
                      <option value="10+0">{t('createRoom.timeControl10_0')}</option>
                      <option value="15+10">{t('createRoom.timeControl15_10')}</option>
                      <option value="30+0">{t('createRoom.timeControl30_0')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-2 block">{t('createRoom.variant')}</label>
                    <select
                      {...register('defaultVariant')}
                      className="w-full px-3 py-2 rounded-xl bg-background border border-border text-body text-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
                    >
                      <option value="standard">{t('createRoom.variantStandard')}</option>
                      <option value="chess960">{t('createRoom.variantChess960')}</option>
                      <option value="atomic">{t('createRoom.variantAtomic')}</option>
                      <option value="horde">{t('createRoom.variantHorde')}</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-2 block">{t('createRoom.stockfishLevel')}</label>
                    <select
                      {...register('defaultStockfishLevel', { valueAsNumber: true })}
                      className="w-full px-3 py-2 rounded-xl bg-background border border-border text-body text-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30"
                    >
                      <option value={1}>{t('createRoom.stockfishLevel1')}</option>
                      <option value={2}>{t('createRoom.stockfishLevel2')}</option>
                      <option value={3}>{t('createRoom.stockfishLevel3')}</option>
                      <option value={4}>{t('createRoom.stockfishLevel4')}</option>
                      <option value={5}>{t('createRoom.stockfishLevel5')}</option>
                    </select>
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        {...register('defaultRated')}
                        className="mr-2 w-4 h-4 text-primary bg-background border-border rounded focus:ring-primary/30 focus:ring-2"
                      />
                      <span className="text-nav text-foreground">{t('createRoom.ratedGames')}</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Arena branding: logo and theme colors (when creating an arena) */}
              {(chessArenaOnly || watch('tournamentType') === 'arena') && (
              <div className="glass-card rounded-xl p-4 border border-border">
                <h3 className="text-display-2 font-semibold text-foreground mb-4">🎨 {t('createRoom.arenaBranding')}</h3>
                <div className="space-y-4">
                  <div>
                    <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-2 block">{t('createRoom.arenaLogo')}</label>
                    <input type="file" ref={arenaLogoInputRef} className="hidden" accept="image/*" onChange={(e) => handleArenaFileUpload(e, setArenaLogo)} />
                    <button type="button" onClick={() => arenaLogoInputRef.current?.click()} className="w-20 h-20 rounded-xl border-2 border-dashed border-border hover:border-primary bg-muted/30 flex items-center justify-center overflow-hidden">
                      {arenaLogo ? <img src={arenaLogo} alt="" className="w-full h-full object-cover" /> : <Upload className="w-6 h-6 text-muted-foreground" />}
                    </button>
                  </div>
                  <div>
                    <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-2 block">{t('createRoom.arenaBanner')}</label>
                    <input type="file" ref={arenaBannerInputRef} className="hidden" accept="image/*" onChange={(e) => handleArenaFileUpload(e, setArenaBanner)} />
                    <button type="button" onClick={() => arenaBannerInputRef.current?.click()} className="w-full h-24 rounded-xl border-2 border-dashed border-border hover:border-primary bg-muted/30 flex items-center justify-center overflow-hidden">
                      {arenaBanner ? <img src={arenaBanner} alt="" className="w-full h-full object-cover" /> : <Image className="w-8 h-8 text-muted-foreground" />}
                    </button>
                  </div>
                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-3 sm:gap-3">
                    <ArenaColorPickerField
                      label={t('createRoom.arenaPrimaryColor')}
                      value={arenaPrimaryColor}
                      onChange={setArenaPrimaryColor}
                      defaultHex={ARENA_DEFAULT_PRIMARY}
                      isOpen={openArenaColorPicker === 'primary'}
                      onOpenChange={(open) => setOpenArenaColorPicker(open ? 'primary' : null)}
                    />
                    <ArenaColorPickerField
                      label={t('createRoom.arenaSecondaryColor')}
                      value={arenaSecondaryColor}
                      onChange={setArenaSecondaryColor}
                      defaultHex={ARENA_DEFAULT_SECONDARY}
                      isOpen={openArenaColorPicker === 'secondary'}
                      onOpenChange={(open) => setOpenArenaColorPicker(open ? 'secondary' : null)}
                    />
                    <ArenaColorPickerField
                      label={t('createRoom.arenaAccentColor')}
                      value={arenaAccentColor}
                      onChange={setArenaAccentColor}
                      defaultHex={ARENA_DEFAULT_ACCENT}
                      isOpen={openArenaColorPicker === 'accent'}
                      onOpenChange={(open) => setOpenArenaColorPicker(open ? 'accent' : null)}
                    />
                  </div>
                  <div
                    className="h-8 rounded-lg flex overflow-hidden border border-border"
                    style={{
                      background: `linear-gradient(90deg, ${normalizeArenaHex(arenaPrimaryColor, ARENA_DEFAULT_PRIMARY)}, ${normalizeArenaHex(arenaSecondaryColor, ARENA_DEFAULT_SECONDARY)})`,
                    }}
                  />
                </div>
              </div>
              )}

              {!chessArenaOnly && (
              <div className="glass-card rounded-xl p-4 border border-border">
                <h3 className="text-display-2 font-semibold text-foreground mb-2">🏆 {t('createRoom.tournamentTypeLabel')}</h3>
                <p className="text-body-lg text-foreground/80 mb-4">
                  {t('createRoom.tournamentTypeDesc')}
                </p>
                <input type="hidden" {...register('tournamentType')} />
                <div className="space-y-3 max-h-[50vh] overflow-y-auto pr-1">
                  {/* None – available soon, not selectable */}
                  <div className="relative w-full rounded-xl p-4 text-left border border-border bg-background/50 overflow-hidden">
                    <div className="blur-[2px] select-none pointer-events-none">
                      <div className="flex items-center gap-3">
                        <span className="text-muted-foreground text-2xl">—</span>
                        <div>
                          <p className="text-nav font-semibold text-foreground">{t('createRoom.tournamentNone')}</p>
                          <p className="text-small text-muted-foreground mt-0.5">{t('createRoom.tournamentNoneDesc')}</p>
                        </div>
                      </div>
                    </div>
                    <div className="absolute inset-0 z-10 flex items-center justify-center">
                      <span className="px-3 py-1.5 rounded-lg bg-foreground/90 text-background text-sm font-semibold shadow-lg">
                        {t('createRoom.availableSoon')}
                      </span>
                    </div>
                  </div>
                  {CHESS_TOURNAMENT_TYPE_CONFIGS.map((config) => {
                    const name = t(`chessTypes.${config.keyPrefix}Name`)
                    const description = t(`chessTypes.${config.keyPrefix}Desc`)
                    const features = [
                      t(`chessTypes.${config.keyPrefix}F1`),
                      t(`chessTypes.${config.keyPrefix}F2`),
                      t(`chessTypes.${config.keyPrefix}F3`),
                      t(`chessTypes.${config.keyPrefix}F4`),
                    ]
                    const isSelectable = config.id === 'arena' || config.id === 'round-robin'
                    const selected = watch('tournamentType') === config.id
                    return (
                      <div key={config.id} className="relative">
                        <button
                          type="button"
                          onClick={() => isSelectable && setValue('tournamentType', config.id as TournamentType)}
                          disabled={!isSelectable}
                          className={`relative w-full rounded-xl p-4 text-left border transition-all group ${
                            !isSelectable ? 'cursor-not-allowed' : ''
                          } ${
                            selected
                              ? `border-primary bg-primary/15 ring-1 ring-primary/30`
                              : `border-border bg-background/80 hover:border-primary/40`
                          } ${!isSelectable ? 'overflow-hidden' : ''}`}
                        >
                          <div className={`absolute inset-0 rounded-xl bg-gradient-to-br ${config.color} transition-opacity pointer-events-none ${selected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}`} style={{ zIndex: 0 }} />
                          <div className={`relative z-10 ${!isSelectable ? 'blur-[2px] select-none pointer-events-none' : ''}`}>
                            <div className="flex items-start gap-3 mb-2">
                              <div className={`w-10 h-10 shrink-0 rounded-xl bg-gradient-to-br ${config.gradient} flex items-center justify-center text-xl`}>
                                {config.icon}
                              </div>
                              <div className="min-w-0 flex-1">
                                <h4 className="text-nav font-semibold text-foreground">{name}</h4>
                                <p className="text-small text-muted-foreground mt-0.5 leading-relaxed">{description}</p>
                              </div>
                            </div>
                            <ul className="space-y-1.5 mt-2 ml-0">
                              {features.map((feature, i) => (
                                <li key={i} className="flex items-start gap-2 text-small text-foreground/80">
                                  <span className={`shrink-0 w-4 h-4 rounded-full bg-gradient-to-br ${config.gradient} flex items-center justify-center mt-0.5`}>
                                    <svg className="w-2.5 h-2.5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                                    </svg>
                                  </span>
                                  <span>{feature}</span>
                                </li>
                              ))}
                            </ul>
                          </div>
                        </button>
                        {!isSelectable && (
                          <div className="absolute inset-0 z-20 flex items-center justify-center rounded-xl pointer-events-none">
                            <span className="px-3 py-1.5 rounded-lg bg-foreground/90 text-background text-sm font-semibold shadow-lg">
                              {t('createRoom.availableSoon')}
                            </span>
                          </div>
                        )}
                      </div>
                    )
                  })}
                </div>
              </div>
              )}
            </>
          )}

          {/* Formula 1-specific fields */}
          {gameType === 'formula1' && (
            <div className="glass-card rounded-xl p-4 border border-border">
              <h3 className="text-display-2 font-semibold text-foreground mb-4">🏎️ {t('createRoom.f1PointsSystem')}</h3>
              <p className="text-body-lg text-foreground/80 mb-4">{t('createRoom.f1PointsDesc')}</p>
              <div className="grid grid-cols-5 gap-3">
                <div>
                  <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-1 block">{t('createRoom.position1st')}</label>
                  <input {...register('position1st', { required: true })} type="number" min="0" className="w-full px-2 py-2 rounded-xl bg-background border border-border text-body text-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30" />
                </div>
                <div>
                  <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-1 block">{t('createRoom.position2nd')}</label>
                  <input {...register('position2nd', { required: true })} type="number" min="0" className="w-full px-2 py-2 rounded-xl bg-background border border-border text-body text-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30" />
                </div>
                <div>
                  <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-1 block">{t('createRoom.position3rd')}</label>
                  <input {...register('position3rd', { required: true })} type="number" min="0" className="w-full px-2 py-2 rounded-xl bg-background border border-border text-body text-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30" />
                </div>
                <div>
                  <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-1 block">{t('createRoom.position4th')}</label>
                  <input {...register('position4th', { required: true })} type="number" min="0" className="w-full px-2 py-2 rounded-xl bg-background border border-border text-body text-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30" />
                </div>
                <div>
                  <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-1 block">{t('createRoom.position5th')}</label>
                  <input {...register('position5th', { required: true })} type="number" min="0" className="w-full px-2 py-2 rounded-xl bg-background border border-border text-body text-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30" />
                </div>
                <div>
                  <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-1 block">{t('createRoom.position6th')}</label>
                  <input {...register('position6th', { required: true })} type="number" min="0" className="w-full px-2 py-2 rounded-xl bg-background border border-border text-body text-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30" />
                </div>
                <div>
                  <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-1 block">{t('createRoom.position7th')}</label>
                  <input {...register('position7th', { required: true })} type="number" min="0" className="w-full px-2 py-2 rounded-xl bg-background border border-border text-body text-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30" />
                </div>
                <div>
                  <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-1 block">{t('createRoom.position8th')}</label>
                  <input {...register('position8th', { required: true })} type="number" min="0" className="w-full px-2 py-2 rounded-xl bg-background border border-border text-body text-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30" />
                </div>
                <div>
                  <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-1 block">{t('createRoom.position9th')}</label>
                  <input {...register('position9th', { required: true })} type="number" min="0" className="w-full px-2 py-2 rounded-xl bg-background border border-border text-body text-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30" />
                </div>
                <div>
                  <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-1 block">{t('createRoom.position10th')}</label>
                  <input {...register('position10th', { required: true })} type="number" min="0" className="w-full px-2 py-2 rounded-xl bg-background border border-border text-body text-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30" />
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-4 pt-4 border-t border-border">
                <div>
                  <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-1 block">{t('createRoom.championAtStart')}</label>
                  <input {...register('championAtStart', { required: true })} type="number" min="0" placeholder="20" className="w-full px-3 py-2 rounded-xl bg-background border border-border text-body text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30" />
                </div>
                <div>
                  <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-1 block">{t('createRoom.championSecondChance')}</label>
                  <input {...register('championSecondChance', { required: true })} type="number" min="0" placeholder="10" className="w-full px-3 py-2 rounded-xl bg-background border border-border text-body text-foreground placeholder:text-muted-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30" />
                </div>
                <div>
                  <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-1 block">{t('createRoom.winnerExtraTimePoints')}</label>
                  <input {...register('winnerAdditionalTime', { required: true })} type="number" min="0" placeholder="2" className="w-full px-3 py-2 rounded-xl bg-background border border-border text-body text-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30" />
                </div>
                <div>
                  <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-1 block">{t('createRoom.winnerPenaltiesPoints')}</label>
                  <input {...register('winnerPenalties', { required: true })} type="number" min="0" placeholder="3" className="w-full px-3 py-2 rounded-xl bg-background border border-border text-body text-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30" />
                </div>
              </div>
            </div>
          )}

          {/* Golf-specific fields */}
          {gameType === 'golf' && (
            <div className="glass-card rounded-xl p-4 border border-border">
              <h3 className="text-display-2 font-semibold text-foreground mb-4">⛳ Golf scoring system</h3>
              <p className="text-body-lg text-foreground/80 mb-4">
                Configure points for golf outcomes and bonus achievements in your room.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div>
                  <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-1 block">Hole-in-one</label>
                  <input {...register('holeInOnePoints', { required: true })} type="number" className="w-full px-2 py-2 rounded-xl bg-background border border-border text-body text-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30" />
                </div>
                <div>
                  <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-1 block">Albatross</label>
                  <input {...register('albatrossPoints', { required: true })} type="number" className="w-full px-2 py-2 rounded-xl bg-background border border-border text-body text-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30" />
                </div>
                <div>
                  <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-1 block">Eagle</label>
                  <input {...register('eaglePoints', { required: true })} type="number" className="w-full px-2 py-2 rounded-xl bg-background border border-border text-body text-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30" />
                </div>
                <div>
                  <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-1 block">Birdie</label>
                  <input {...register('birdiePoints', { required: true })} type="number" className="w-full px-2 py-2 rounded-xl bg-background border border-border text-body text-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30" />
                </div>
                <div>
                  <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-1 block">Par</label>
                  <input {...register('parPoints', { required: true })} type="number" className="w-full px-2 py-2 rounded-xl bg-background border border-border text-body text-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30" />
                </div>
                <div>
                  <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-1 block">Bogey</label>
                  <input {...register('bogeyPoints', { required: true })} type="number" className="w-full px-2 py-2 rounded-xl bg-background border border-border text-body text-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30" />
                </div>
                <div>
                  <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-1 block">Double bogey</label>
                  <input {...register('doubleBogeyPoints', { required: true })} type="number" className="w-full px-2 py-2 rounded-xl bg-background border border-border text-body text-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30" />
                </div>
                <div>
                  <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-1 block">Fairway hit</label>
                  <input {...register('fairwayHitPoints', { required: true })} type="number" className="w-full px-2 py-2 rounded-xl bg-background border border-border text-body text-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30" />
                </div>
                <div>
                  <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-1 block">Longest drive bonus</label>
                  <input {...register('longestDriveBonus', { required: true })} type="number" className="w-full px-2 py-2 rounded-xl bg-background border border-border text-body text-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30" />
                </div>
                <div>
                  <label className="text-small font-semibold text-foreground/90 uppercase tracking-wider mb-1 block">Closest to pin bonus</label>
                  <input {...register('closestToPinBonus', { required: true })} type="number" className="w-full px-2 py-2 rounded-xl bg-background border border-border text-body text-foreground focus:outline-none focus:border-primary focus:ring-2 focus:ring-primary/30" />
                </div>
              </div>
            </div>
          )}

          {/* Submit Buttons */}
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-3 rounded-xl font-semibold bg-background/80 border border-border text-foreground hover:bg-muted/30 transition-colors"
            >
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={isLoading}
              className="flex-1 px-4 py-3 rounded-xl font-semibold bg-primary text-primary-foreground hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed transition-opacity"
            >
              {isLoading ? t('createRoom.creating') : (chessArenaOnly ? t('createRoom.createArena') : t('dashboard.createRoom'))}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

export default CreateRoomModal
